import {
  MIDDOT,
  type ScheduleRow,
  dayOfSlot,
  displayEndOfSlot,
  formatDayShort,
  formatSlotTime,
  slotOfDay,
} from '@betreuung/shared';
import { PersonChip } from '@/components/shared/PersonChip';
import { de } from '@/i18n/de';
import { personVars } from '@/lib/personColor';
import type { PersonLookup } from '@/lib/persons';
import { cn } from '@/lib/utils';

/** The two schedule rows that describe the present; both render as the card. */
export type RunningRow = Extract<ScheduleRow, { kind: 'running' | 'unassignedNow' }>;

/** `Sa, 01.08., 10:00` */
function moment(dayIndex: number, slotInDay: number): string {
  return `${formatDayShort(dayIndex)}, ${formatSlotTime(slotInDay)}`;
}

function startMoment(slot: number): string {
  return moment(dayOfSlot(slot), slotOfDay(slot));
}

/** Only an end folds a day boundary back onto `24:00` of the day before. */
function endMoment(slot: number): string {
  const { dayIndex, slotInDay } = displayEndOfSlot(slot);
  return moment(dayIndex, slotInDay);
}

const CARD = 'mx-4 mb-3 rounded-panel border p-3.5';
const LABEL = 'pb-1 font-mono text-tab font-semibold tracking-[.12em]';

/**
 * The highlighted head of the "Aktuell" tab: who has the child right now, until
 * when, and who takes over. Tinted in the person's colour so the answer is
 * readable before any text is.
 */
export function RunningCard({ row, persons }: { row: RunningRow; persons: PersonLookup }) {
  if (row.kind === 'unassignedNow') {
    return (
      <section className={cn(CARD, 'border-line bg-muted')}>
        <p className={cn(LABEL, 'text-fg-muted')}>{de.current.nothingRunning}</p>
        <div className="flex items-center justify-between gap-2.5">
          <span className="text-month font-semibold text-fg">
            {row.untilSlot === null
              ? de.current.openEnded
              : de.current.until(endMoment(row.untilSlot))}
          </span>
          <PersonChip person={null} />
        </div>
      </section>
    );
  }

  const person = persons.describe(row.assignment.personId);
  const nextName =
    row.nextPersonId === null ? de.modal.unassigned : persons.describe(row.nextPersonId).name;

  return (
    <section
      style={personVars(person.colorIndex)}
      className={cn(CARD, 'person-tint-soft person-border-soft')}
    >
      <p className={cn(LABEL, 'text-(--person)')}>{de.current.runningLabel}</p>
      <div className="flex items-center justify-between gap-2.5">
        <span className="text-month font-semibold text-fg">
          {de.current.until(endMoment(row.assignment.endSlot))}
        </span>
        <PersonChip person={person} label={de.current.with(person.name)} />
      </div>
      <p className="pt-1.5 text-chip text-fg-muted">
        {`${de.current.since(startMoment(row.assignment.startSlot))} ${MIDDOT} ${de.current.next(nextName)}`}
      </p>
    </section>
  );
}
